import { DateTime } from "luxon"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Button, Modal } from "react-bootstrap"
import { CloudUpload, Save } from "react-bootstrap-icons"
import { FileUploader } from "react-drag-drop-files"
import { MissionControlOrderDataBlob } from "../../common/common.interfaces"
import CustomError, { CustomErrorProps } from "../../common/components/CustomError"
import { useLoading } from "../../loading/LoadingContext"
import { useUserContext } from "../../user/UserContext"
import { apiFetch } from "../../utility/fetch"
import SimpleCrystalBallModal from "../../common/components/SimpleCrystalBallModal"

interface EpicOrdersImportStartProps {
  handleNewOrdersUpload: any
  finishOrdersUpload: any
  mappedOrders: MissionControlOrderDataBlob[] // successfully mapped to MC format
}

export default function EpicOrdersImportStart(props: EpicOrdersImportStartProps) {

  const [excelFile, setExcelFile] = useState(undefined)
  const [importError, setImportError] = useState(undefined)
  const [showModal, setShowModal] = useState(false)
  const [errors, setErrors] = useState([])
  const [timestamp, setTimestamp] = useState(DateTime.now())

  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  

  const {userData} = useUserContext()
  const EPIC_IMPORT_OS_PATH = 'order-service/inbound/new/epic'

  // #region LOAD INTO ORDER SERVICE
  const handleSaveClicked = useCallback(async () => {
    changeStep('Uploading Blobs to Order Service..')
    changeSubStep('Saving...')
    toggleLoading(true)
    for ( const orderBlob of props.mappedOrders ) {
      console.log(JSON.stringify(orderBlob))
      const saveResultBlobName = `${EPIC_IMPORT_OS_PATH}/${orderBlob.order.Name}.json`
      const saveResultResponse = await apiFetch({
        endpoint: 'blob',
        method: 'POST',
        accessToken: userData.accessToken,
        body: JSON.stringify(orderBlob),
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: saveResultBlobName}
        ]
      })
      if(!saveResultResponse.ok){
        setImportError({
          content: saveResultResponse.body,
          description: `Failed to save mapped orders file.`,
          title: 'Order Services Saving Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
    }
    toggleLoading(false)
    props.finishOrdersUpload()
    setShowModal(true)
    return
  }, [props.mappedOrders])

  const handleExcelFileUpload = useCallback((file: File) => {
    setExcelFile(file)
  }, [])

  const handleClose = () => {
    setShowModal(false);
  };

  /**
   * I had to use this in order to delay processing the file until it's actually set
   */
  const processExcelFile = useEffect(() => {
    if(!excelFile) { // will trigger when the page is rendered or excelFile is cleared, so ignore it unless we actually have a file
      return
    }
    setImportError(undefined)
    setTimestamp(DateTime.now())
    const errors = []
    if(!excelFile){
      errors.push('Original Excel upload is missing.')
    }
    setErrors(errors)
    if(errors.length > 0){
      const message = errors.map(e => (
        <><span>{e} </span></>
      ))
      setImportError({
        content: message,
        description: message,
        title: 'Import Validation Errors',
        visible: true
      } as CustomErrorProps)
      return
    }

    const response = {
      timestamp,
      excelFile
    }
    props.handleNewOrdersUpload(response)
    setExcelFile(undefined)
    setImportError(undefined)
  }, [excelFile]);

  
  const handleCloseImportError = useCallback((e) => {
    setImportError({
      visible: false
    })
  }, [])

  const view = useMemo(() => {
    return <div>
      <span className='beacon-header'>UPLOAD FILES</span>
      <hr/>
      <div style={{display: 'flex', alignItems: 'stretch', justifyContent: 'center'}}>
        {!excelFile && 
          <FileUploader 
              handleChange={handleExcelFileUpload} 
              name="file" 
              types={['xlsx','xls']}
            >
              <div className='import-panel warning flex-fill'>
                <div className="import-title">UPLOAD EXCEL</div>
                <CloudUpload height={50} width={50} />
                <div className="import-subtext">Drag & Drop / Click</div>
              </div>
            </FileUploader>
        }
        {excelFile &&
          <FileUploader 
              handleChange={handleExcelFileUpload} 
              name="file" 
              types={['xlsx','xls']}
            >
              <div className='import-panel success flex-fill'>
                <div className="import-title">UPLOAD EXCEL</div>
                <CloudUpload height={50} width={50} />
                <div className="import-subtext">{excelFile.name}</div>
              </div>
            </FileUploader>
        }
        <Button onClick={handleSaveClicked} disabled={(!props.mappedOrders || props.mappedOrders.length === 0)} variant='success' size='lg' className="import-panel success text-wrap fw-bold py-3 text-dark" style={{fontSize: '14px'}}><Save/>&nbsp;&nbsp;SUBMIT ORDERS TO MISSION CONTROL</Button>
      </div>
      <SimpleCrystalBallModal content={<>Orders were uploaded to Order Services!<br/>Verify orders are accurate in Mission Control.</>} show={showModal} toggleModal={handleClose} />
      <hr/>       
      <CustomError closeError={handleCloseImportError} content={importError?.content} description={importError?.description} title={importError?.title} visible={importError?.visible} />
    </div>
  }, [importError, timestamp, excelFile, props.mappedOrders, showModal, errors])

  return view

}
